/*
 * Shared page assembly — everything except the language.
 *
 * Both entry points (main.ts for English, main.da.ts for Danish) call
 * mountPage with their dictionary and the href of the other language's
 * page, for the language toggle. Everything below is language-agnostic:
 * section order, the Three.js scene lifecycle, the scroll spy.
 */
import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/sections.css";

import type { LogoScene } from "./three/logoScene";
import type { PageContent } from "./i18n/types";
import { renderHero } from "./sections/hero";
import { renderLogoWall } from "./sections/logoWall";
import { renderPartners } from "./sections/partners";
import { renderPillars } from "./sections/pillars";
import { renderWorksFor } from "./sections/worksFor";
import { renderFeatures } from "./sections/features";
import { renderCustomizeGrid } from "./sections/customizeGrid";
import { renderPricing } from "./sections/pricing";
import { renderResources } from "./sections/resources";
import { renderFAQ } from "./sections/faq";
import { renderFinalCTA } from "./sections/finalCTA";
import { renderColophon } from "./sections/colophon";
import { renderThemeToggle } from "./sections/themeToggle";
import { renderLangToggle } from "./sections/langToggle";
import { renderSectionNav, initSectionNav } from "./sections/sectionNav";

export function mountPage(content: PageContent, langToggleHref: string): void {
  const app = document.getElementById("app");
  if (!app) throw new Error("Missing #app root");

  const hero = renderHero(content.hero);
  app.appendChild(hero.root);
  app.appendChild(renderLogoWall(content.logoWall));
  app.appendChild(renderPartners(content.partners));

  const sections = [
    renderPillars(content.pillars),
    renderWorksFor(content.worksFor),
    renderFeatures(content.features),
    renderCustomizeGrid(content.customizeGrid),
    renderPricing(content.pricing),
    renderResources(content.resources),
    renderFAQ(content.faq),
    renderFinalCTA(content.finalCta),
  ];

  for (const section of sections) {
    app.appendChild(section);
  }

  /* Colophon: mounted on <body> rather than inside <main> so it lands as a
   * proper contentinfo landmark — the last word of the page. */
  document.body.appendChild(renderColophon(content.colophon));

  /* Theme toggle: fixed to the viewport, so it also mounts on <body>. */
  document.body.appendChild(renderThemeToggle(content.themeToggle));

  /* Language toggle: beside the theme toggle, bottom-left. */
  document.body.appendChild(renderLangToggle(content.langToggle, langToggleHref));

  /* ----- Section nav ------------------------------------------------- *
   * A fixed vertical dot rail mapping the page. Mounted last so it sits
   * above the content, initialized after every section exists so the
   * scroll spy can observe them all. initSectionNav returns a teardown
   * used on unload. */
  const sectionNav = renderSectionNav(content.sectionNav);
  app.appendChild(sectionNav);
  let teardownSectionNav = initSectionNav(sectionNav);

  /* ----- Three.js scene lifecycle ------------------------------------ *
   * The hero owns the canvas mount. The scene sizes itself via a
   * ResizeObserver on the mount (see logoScene.ts), so no window-resize
   * wiring or initial resize call is needed here. The render loop pauses
   * (start/stop) when the hero scrolls out of view so the GPU can sleep.
   * We dispose on unload.
   *
   * three.js is imported DYNAMICALLY: it is by far the heaviest dependency
   * (~465 kB minified) and only the hero logo needs it, so it is split into
   * its own chunk that loads after first paint rather than blocking the
   * initial render. The type import above is erased at compile time and
   * keeps the LogoScene typing without pulling three.js into the main chunk.
   */
  const canvasMount = hero.canvasMount;
  const prefersReducedMotion =
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

  let logoScene: LogoScene | null = null;

  const createScene = (factory: (mount: HTMLElement, opts: { reducedMotion: boolean }) => LogoScene): void => {
    logoScene = factory(canvasMount, {
      reducedMotion: prefersReducedMotion,
    });
    logoScene.start();
  };

  requestAnimationFrame(() => {
    import("./three/logoScene")
      .then(({ createLogoScene }) => {
        createScene(createLogoScene);
      })
      .catch((err) => {
        console.error("Failed to load the 3D logo scene:", err);
      });
  });

  let wasVisible = true;
  const heroObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const visible = entry.isIntersecting;
        if (visible && !wasVisible) {
          logoScene?.start();
          wasVisible = true;
        } else if (!visible && wasVisible) {
          logoScene?.stop();
          wasVisible = false;
        }
      }
    },
    { threshold: 0.05 },
  );
  heroObserver.observe(hero.root);

  /* ----- Page lifecycle ---------------------------------------------- *
   * pagehide is the bfcache-friendly teardown event: unlike beforeunload,
   * registering it does not prevent the browser from freezing the page into
   * the back/forward cache. When the user navigates away we release the GPU
   * resources; if the page is then restored from bfcache, pageshow
   * (persisted) below rebuilds everything. */
  window.addEventListener("pagehide", (event) => {
    logoScene?.dispose();
    logoScene = null;
    heroObserver.disconnect();
    teardownSectionNav();
    void event;
  });

  /* ----- Back/forward cache ------------------------------------------ *
   * When the user navigates away, pagehide disposes the scene — but the
   * browser may then freeze the page into the back/forward cache anyway.
   * Pressing back restores that disposed state: the canvas is gone and
   * nothing re-creates it. pageshow with persisted=true fires exactly then,
   * so we rebuild the scene and re-arm the observers. The dynamic import is
   * already in the module cache, so this is cheap. */
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) return;
    if (logoScene) return; // already alive (e.g. duplicate event)
    import("./three/logoScene")
      .then(({ createLogoScene }) => {
        createScene(createLogoScene);
        // The observers were disconnected on pagehide; re-arm them so the
        // pause-when-offscreen behaviour and the scroll spy survive the
        // restore. initSectionNav is safe to re-run after its teardown
        // removed the listeners; keep the fresh teardown function.
        wasVisible = true;
        heroObserver.observe(hero.root);
        teardownSectionNav = initSectionNav(sectionNav);
      })
      .catch((err) => {
        console.error("Failed to restore the 3D logo scene:", err);
      });
  });
}
